/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/**
 * 通用臻有钱路由管理：授信、查看授信进度、提现、还款 等
 */
import '@/views_v2/home/store';
import { Component, Mixins } from 'vue-property-decorator';
import { RouterV2Enum } from '@/router/router-names';
import { UserInfoStatus } from '@/enums/UserInfoStatus';
import platform from '@/shared/utils/platform';
import HomeV2Status, { getUserInfoProgress } from '@/api/homeV2Status';
import { platformIdentityManager } from '@/shared/identity-verification';
import { AuthSceneTypeEnum } from '@/shared/identity-verification/types';
import { creditTaskStatusTypeEnum, creditTaskSuspendSceneEnum, UpgradeStateEnum } from '@/enums/autarkyUpgrade';
import jumpToVerifyCredit, { checkVerifyCreditCompleteByScene } from '@/shared/verify-credit-controller';
import { OpenVerifyPageMode, VerifyQueryParamsTypeEnum, VerifySceneEnum } from '@/shared/verify-credit-controller/types';
import { namespace } from 'vuex-class';
import type { AutarkyStateType, CreditDataType } from '@/api/homeV2Status/types';
import { jumpToNewVersion } from '@/shared/utils/jumpTo';
import { NewVersionEnum } from '@/enums/newVersionRoute';
import { agreementService } from '@/service/confirm-agreements';
import type { StateType as BaseStateType } from '@/store/base/types';
import IdenttityScoreMixins from '../finance-identity';

const HomeStore = namespace('home');
const BaseStore = namespace('base');

@Component
export default class FinanceBusiness extends Mixins(
  IdenttityScoreMixins,
) {
  @BaseStore.State businessInfoExtra!: BaseStateType['businessInfoExtra'];

  @HomeStore.State creditData!: CreditDataType;

  @HomeStore.State canApplyCredit!: boolean;

  @HomeStore.State autarkyState!: AutarkyStateType;

  @HomeStore.Action getCreditData!: () => Promise<void>;

  // 检查是否需要完善、更新 基本信息资料页数据（月收入，教育情况，紧急联系人相关）
  private get isNeedCheckBaseInfo(): boolean {
    const { needEducation, needMonthSalary, needEmergencyContact } = this.creditData || {};
    if (!(platform.isAlipayMini || platform.isDingTalk)) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return Boolean(needEducation || needMonthSalary || needEmergencyContact);
    }
    return Boolean(needEducation || needEmergencyContact);
  }

  /** 判断当前页面是否是提现申请页 */
  private get isApplyPage(): boolean {
    return window.location.hash.split('?')[0].indexOf(RouterV2Enum.applyProduct) > -1;
  }

  /**
   * 预授信-进入提现流程前置是否实名检验
   * @param isRouterJump boolean 首页-点击“去提现”: true，提现-进入绑卡: false
   * @returns Boolean值：是否增加了进入 VerifyCredit 页面的路由记录
   */
  protected async preCreditBeforeEnterApplyCheck(isRouterJump = false): Promise<boolean> {
    const hasVerified = await checkVerifyCreditCompleteByScene(
      VerifySceneEnum.PRE_CREDIT_FIRST_INFO,
    );

    if (!hasVerified) {
      return new Promise((resolve, reject) => {
        jumpToVerifyCredit(
          VerifySceneEnum.PRE_CREDIT_FIRST_INFO,
          {
            openMode: isRouterJump ? OpenVerifyPageMode.ROUTER_JUMP : OpenVerifyPageMode.POPUP_SHOW,
            successCb: () => resolve(isRouterJump),
            failCb: () => reject(),
          },
        );
      });
    }

    return Promise.resolve(false);
  }

  /**
   * 检查用户资料是否填写完整，不完整就跳资料页补充(不区分是否h5环境)
   * @param commodityIds 商品id
   * @param isApplyNoBankCard 是否申请无银行卡，h5活体后重新加载当前页面(外采场景: popup模式)
   * @param isPreCredit 是否预授信
   * @returns Promise<boolean> 是否填写完整
   * */
  public checkOuterChannel({
    commodityIds = [],
    isApplyNoBankCard = false,
    isPreCredit = false,
    ocrAfterLoan = false,
  }:{
    commodityIds?: string[],
    isApplyNoBankCard?: boolean
    isPreCredit?: boolean,
    ocrAfterLoan?: boolean,
  } = {}):Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      const creditInfo = await getUserInfoProgress(this.isApplyPage);

      /** 所有资料填写完成，退出 */
      if (creditInfo.fillInTheInformation === UserInfoStatus.Completed) {
        resolve(true);
        return;
      }

      // eslint-disable-next-line no-nested-ternary
      const type = this.isApplyPage
        ? (isApplyNoBankCard
          ? VerifyQueryParamsTypeEnum.ApplyForNoBankCard
          : VerifyQueryParamsTypeEnum.ApplyProduct)
        : VerifyQueryParamsTypeEnum.CardBind;

      /** 若是资料不完整，则进行资料填写，其内部有兼容极简、非极简流程的逻辑 */
      jumpToVerifyCredit(
        isPreCredit
          ? VerifySceneEnum.PRE_CREDIT_ADD_INFO
          : VerifySceneEnum.LOAN_UNION_SUPPLEMENT,
        {
          openMode: OpenVerifyPageMode.POPUP_SHOW,
          commodityIds,
          queryParams: {
            type,
            closeBackConfirmDialog: true,
            ocrAfterLoan,
          },
          successCb: () => resolve(true),
          failCb: () => reject(),
        },
      );
    });
  }

  // 授信申请页面
  private goPreApplyChoose(useReplace = false) {
    const actionName = useReplace ? 'replace' : 'push';
    this.$router[actionName]({
      path: RouterV2Enum.preApplyChoose,
    });
  }

  // 跳转到臻有钱授信申请结果页
  private goPreApplyResult(taskId: string, useReplace = false) {
    this.$router[useReplace ? 'replace' : 'push']({
      query: {
        taskId,
      },
      path: RouterV2Enum.preApplyResult,
    });
  }

  // 是否有可用额度 申请过额度且可用额度大于0
  protected get hasCredit(): boolean {
    return !!(
      this.creditData
      && this.creditData.hasEnter
      && Number(this.creditData.creditAmount) > 0
    );
  }

  // 是否有待还订单，包括本期待还以及逾期订单
  protected get hasOrderWaitForRepay(): boolean {
    return !!(this.creditData && Number(this.creditData.needRepayOrderNum) > 0);
  }

  // 是否有逾期订单
  protected get hasOrderOverdue(): boolean {
    return !!(this.creditData && Number(this.creditData.overdueNum) > 0);
  }

  // 是否获取到了额度且同意了服务协议
  protected async isCreditNotNullAndAgreementsConfirmed(
    buttonName?: string,
  ): Promise<boolean> {
    /** TODO 感觉这里不需要依赖this.creditData 影响面太大，暂时不调整 */
    return !!(
      this.creditData && (await agreementService.confirmFinanceAgreements())
    );
  }

  // 老用户是否需要补充学历信息
  protected isNeedEducation(buttonName: string, otherProps: string[], useReplace = false): void {
    this.$ubtPageButton(buttonName, ...otherProps);
    this.goPreApplyOrBaseInfo(useReplace);
  }

  /**
   * 跳转授信页面，并判断资料是否填写完整，未完整的情况下，跳转基本信息页
   * 极简流程 核心改造申请额度的逻辑
   * @param useReplace 跳转是否使用 replace ，默认 false 使用 push
   */
  private async goPreApplyOrBaseInfo(useReplace = false): Promise<void> {
    let homeStatus;
    try {
      // 获取用户金融信息, 通过3个接口拼接
      const creditCombineData = await Promise.all([
        HomeV2Status.getUserCredit(),
        HomeV2Status.getUserLoan(),
        HomeV2Status.getUserInfoProgress(),
      ]);
      /** 数据拼接 */
      const creditDataCombine = {
        ...(creditCombineData[0]).businessData,
        ...creditCombineData[1],
        ...creditCombineData[2],
      };
      homeStatus = creditDataCombine;
    } catch {
      homeStatus = {};
    }
    const {
      fillInTheInformation,
      firstInfoComplete,
      creditTaskStatus,
      progressingTaskId,
      suspendScene,
    } = this.creditData || homeStatus || {};

    if (this.confirmShouldIdentityScore()) {
      /** 有 进行中授信任务 id */
      if (progressingTaskId) {
        /** 有进行中授信任务 id，且任务挂起状态（自营 B 失败，联营未补充资料） */
        /** 评估是否可以删除，统一通过判断完整资料是否填写完整来做页面跳转 */
        if (creditTaskStatus === creditTaskStatusTypeEnum.HANG) {
          // 跳转信息页新路由，提现前补充资料填写
          jumpToVerifyCredit(
            suspendScene === creditTaskSuspendSceneEnum.AUTARKY_OCR_SUSPEND
              ? VerifySceneEnum.CREDIT_MIDDLE_ONLY_OCR
              : VerifySceneEnum.CREDIT_POST_SUPPLEMENT,
            { creditId: progressingTaskId },
          );
        } else {
          /** 资料填写完整场景，跳审核中页面 */
          if (fillInTheInformation === UserInfoStatus.Completed) {
            jumpToNewVersion(NewVersionEnum.CREDIT_RESULT, useReplace, `taskId=${progressingTaskId}`);
          } else {
            jumpToVerifyCredit(VerifySceneEnum.CREDIT_POST_SUPPLEMENT,
              {
                creditId: progressingTaskId,
              });
          }
        }
      } else {
        /** 无进行中授信任务 */
        if (firstInfoComplete) {
          if (this.isNeedCheckBaseInfo) {
            /** 跳转资料页进行：完善、更新 基本信息资料页数据（月收入，教育情况，紧急联系人相关） */
            const {
              needEducation, needMonthSalary, needEmergencyContact,
            } = this.creditData || {};

            jumpToVerifyCredit(VerifySceneEnum.USER_BASE_CONTACT_SUPPLEMENT, {
              useReplace,
              queryParams: {
                needEducation,
                needMonthSalary,
                needEmergencyContact,
              },
            });
          } else {
            /** 流量分发前 ocr 活体资料填写完成，跳转流量分发 */
            this.goPreApplyChoose(useReplace);
          }
        } else {
          // 跳转信息页新路由，流量分发前资料填写场景
          jumpToVerifyCredit(VerifySceneEnum.CREDIT_PRE_VERIFY);
        }
      }
    }
  }

  // 申请额度 需判断用户协议是否填写
  /**
   * 需要区分是否极简流程 以及极简流程时，区分场景，首页按钮跳转逻辑场景 需要区分
   */
  protected async onApplyCredit(
    buttonName?: string,
    ...otherProps: string[]
  ): Promise<void> {
    if (buttonName) {
      this.$ubtPageButton(buttonName, ...otherProps);
    }
    // 已经获取到额度并且同意了协议，但是不需要重新认证
    // 无需处理 else 场景，授权协议同意才会进入跳转逻辑
    const isAgreementsConfirmed = await this.isCreditNotNullAndAgreementsConfirmed(buttonName);
    const isUserAuth = await platformIdentityManager.checkUserAuth(AuthSceneTypeEnum.PRE_LENDING);

    if (isAgreementsConfirmed && isUserAuth) {
      this.goPreApplyOrBaseInfo();
    }
  }


  // 申请额度 无需判断协议是否同意
  protected onApplyCreditWithoutAgreement(
    buttonName: string,
    ...otherProps: string[]
  ): void {
    this.$ubtPageButton(buttonName, ...otherProps);
    this.goPreApplyOrBaseInfo();
  }

  // 查看额度申请审批进度
  protected async onShowApplyCreditProgress(
    buttonName: string,
    ...otherProps: string[]
  ): Promise<void> {
    this.$ubtPageButton(buttonName, ...otherProps);

    if (await this.isCreditNotNullAndAgreementsConfirmed(buttonName)) {
      const taskId = String(this.creditData.progressingTaskId);
      this.goPreApplyResult(taskId);
    }
  }

  // 提现
  protected async onApplyProduct(
    buttonName?: string,
    ...otherProps: string[]
  ): Promise<void> {
    if (buttonName) {
      this.$ubtPageButton(buttonName, ...otherProps);
    }

    /** 检查协议是否已同意 */
    const isAgreementsConfirmed = await this.isCreditNotNullAndAgreementsConfirmed(buttonName);

    if (this.creditData?.preCredit) {
      if (isAgreementsConfirmed) {
        /** 预授信场景: 进入提现流程 && 前置补充上传OCR信息 */
        const hasAddRouteHistory = await this.preCreditBeforeEnterApplyCheck(true);
        /** 若是有资料填写路由记录，进入提现页前需使用replace替换记录，避免back返回时跳回Verify页面 */
        this.$router[hasAddRouteHistory ? 'replace' : 'push']({
          path: RouterV2Enum.applyProduct,
        });
      }
    } else {
      /** 非预授信 */
      if (isAgreementsConfirmed) {
        this.$router.push({
          path: RouterV2Enum.applyProduct,
        });
      }
    }
  }

  /** 预授信-进入提现流程，首页-点击“去提现” */
  protected async onPreCreditApplyProduct(
    buttonName: string,
    otherProps: Dictionary,
  ): Promise<void> {
    this.$ubtPageButtonNew(buttonName, otherProps);

    /** 检查协议是否已同意 */
    const isAgreementsConfirmed = await this.isCreditNotNullAndAgreementsConfirmed(buttonName);

    if (isAgreementsConfirmed) {
      /** 预授信场景: 进入提现流程 && 前置补充上传Ocr信息 */
      const hasAddRouteHistory = await this.preCreditBeforeEnterApplyCheck(true);
      /** 若是有资料填写路由记录，进入提现页前需使用replace替换记录，避免back返回时跳回Verify页面 */
      this.$router[hasAddRouteHistory ? 'replace' : 'push']({
        path: RouterV2Enum.applyProduct,
      });
    }
  }

  // 去还款(查看账单)
  protected onRepay(
    buttonName: string,
    ...otherProps: string[]
  ): void {
    this.$ubtPageButton(buttonName, ...otherProps);
    this.$router.push({
      path: RouterV2Enum.myBill,
    });
  }

  // 查看我的审批列表页面
  protected showPreApplyPage(
    buttonName: string,
    ...otherProps: string[]
  ): void {
    this.$ubtPageButton(buttonName, ...otherProps);

    this.$router.push({
      path: RouterV2Enum.myPreApply,
    });
  }

  // 查看提现记录
  protected showMyApplyPage(
    buttonName: string,
    tabIndex: number | string = 0,
    from = 'home',
    ...otherProps: string[]
  ): void {
    this.$ubtPageButton(buttonName, ...otherProps);
    this.$router.push({
      path: RouterV2Enum.myApply,
      query: {
        from,
        index: String(tabIndex),
      },
    });
  }

  // 查看还款记录
  protected showRepayRecordsPage(
    buttonName: string,
    ...otherProps: string[]
  ): void {
    this.$ubtPageButton(buttonName, ...otherProps);
    this.$router.push({
      path: RouterV2Enum.repayRecords,
    });
  }

  /** 隐藏自营 A、B 场景 授信成功用户，其他授信入口 */
  protected isAutarkySucceed(): boolean {
    if (this.canApplyCredit !== undefined) {
      return !this.canApplyCredit;
    }
    return false;
  }

  /** 是否圈可自营，不判断autarkyUpgradeState */
  protected isCircleAutarky(): boolean {
    return this.autarkyState && this.autarkyState.autarky;
  }

  /** 是否圈中自营老户 */
  protected isAutarkyUser(): boolean {
    return this.autarkyState
    && this.autarkyState.autarky
    && this.autarkyState.autarkyUpgradeState === UpgradeStateEnum.INIT;
  }

  /** 极简流程 资料未填或者完整填完，可申请额度场景 */
  protected get canReceiveAmount(): boolean {
    const {
      firstInfoComplete,
      firstInfoFillInTheInformation,
    } = this.creditData || {};

    /** 流量分发前 资料完全未填写 、资料已填写完整 */
    return firstInfoFillInTheInformation === UserInfoStatus.Init || firstInfoComplete as boolean;
  }
}
